import React, { useState, useEffect, useCallback } from 'react';
import { MdBookmark, MdDeleteOutline, MdOpenInNew, MdBookmarkBorder } from 'react-icons/md';
import EmotionBars, { simulateExpressions } from '../components/EmotionBars/EmotionBars';
import './BookmarksPage.css';

// ── Bookmark Storage Helpers ─────────────────────────────────────
const BOOKMARK_KEY = 'emonews-bookmarks';

const getBookmarks = () => {
  try { return JSON.parse(localStorage.getItem(BOOKMARK_KEY) || '[]'); }
  catch { return []; }
};

// ── Bookmarked Article Card ───────────────────────────────────────
const BookmarkCard = ({ article, index, onRemove }) => {
  const expressions = simulateExpressions('happy', index + 100); // stable seed

  return (
    <div className="bm-card animate-fade-in" style={{ animationDelay: `${index * 0.04}s` }} role="article">
      {/* Image */}
      {article.urlToImage && (
        <div className="bm-card__img">
          <img src={article.urlToImage} alt={article.title || 'Article'} loading="lazy" />
        </div>
      )}

      <div className="bm-card__body">
        {/* Source + Date */}
        <div className="bm-card__meta">
          {article.source?.name && <span className="bm-card__source">{article.source.name}</span>}
          {article.bookmarkedAt && (
            <span className="bm-card__date">
              {new Date(article.bookmarkedAt).toLocaleDateString('en-IN', {
                day: 'numeric', month: 'short', year: 'numeric'
              })}
            </span>
          )}
        </div>

        {/* Title */}
        <a
          href={article.url}
          target="_blank"
          rel="noopener noreferrer"
          className="bm-card__title"
          aria-label={`Read full article: ${article.title}`}
        >
          {article.title}
        </a>

        {/* Description */}
        {article.description && (
          <p className="bm-card__desc">{article.description}</p>
        )}

        {/* Emotion Bars */}
        <div className="bm-card__emotions">
          <EmotionBars expressions={expressions} compact />
        </div>

        {/* Actions */}
        <div className="bm-card__actions">
          <a
            href={article.url}
            target="_blank"
            rel="noopener noreferrer"
            className="bm-btn bm-btn--read"
            aria-label="Open article in new tab"
          >
            <MdOpenInNew />
            Read Article
          </a>
          <button
            className="bm-btn bm-btn--remove"
            onClick={() => onRemove(article.url)}
            aria-label="Remove bookmark"
          >
            <MdDeleteOutline />
            Remove
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Empty State ───────────────────────────────────────────────────
const EmptyState = () => (
  <div className="bm-empty animate-fade-in">
    <div className="bm-empty__icon">
      <MdBookmarkBorder aria-hidden="true" />
    </div>
    <h2 className="bm-empty__title">No Bookmarks Yet</h2>
    <p className="bm-empty__text">
      Start scanning your emotion and bookmark articles that resonate with you.
      They'll appear here for offline reading.
    </p>
    <a href="/scanner" className="btn-primary" aria-label="Go to English scanner">
      Start Scanning
    </a>
  </div>
);

// ── Main Page ─────────────────────────────────────────────────────
const BookmarksPage = () => {
  const [bookmarks, setBookmarks] = useState(() => getBookmarks());
  const [search, setSearch] = useState('');

  // Sync with localStorage changes (from other tabs/scanner pages)
  useEffect(() => {
    const handleStorage = () => setBookmarks(getBookmarks());
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const handleRemove = useCallback((url) => {
    const updated = bookmarks.filter(b => b.url !== url);
    localStorage.setItem(BOOKMARK_KEY, JSON.stringify(updated));
    setBookmarks(updated);
    window.dispatchEvent(new Event('storage'));
  }, [bookmarks]);

  const handleClearAll = () => {
    if (window.confirm('Clear all bookmarks?')) {
      localStorage.setItem(BOOKMARK_KEY, '[]');
      setBookmarks([]);
      window.dispatchEvent(new Event('storage'));
    }
  };

  const filtered = bookmarks.filter(b =>
    b.title?.toLowerCase().includes(search.toLowerCase()) ||
    b.description?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="bm-page">
      {/* Header */}
      <div className="bm-page__header animate-fade-in">
        <div className="bm-page__header-left">
          <div className="bm-page__icon">
            <MdBookmark aria-hidden="true" />
          </div>
          <div>
            <h1 className="bm-page__title">Saved Articles</h1>
            <p className="bm-page__subtitle">
              {bookmarks.length} bookmark{bookmarks.length !== 1 ? 's' : ''} · Works offline
            </p>
          </div>
        </div>
        {bookmarks.length > 0 && (
          <button
            className="bm-clear-btn"
            onClick={handleClearAll}
            aria-label="Clear all bookmarks"
          >
            Clear All
          </button>
        )}
      </div>

      {/* Search */}
      {bookmarks.length > 0 && (
        <div className="bm-search animate-fade-in">
          <input
            type="search"
            placeholder="Search your bookmarks..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="bm-search__input"
            aria-label="Search bookmarks"
          />
        </div>
      )}

      {/* Content */}
      {bookmarks.length === 0 ? (
        <EmptyState />
      ) : filtered.length === 0 ? (
        <div className="bm-empty animate-fade-in">
          <div className="bm-empty__icon">🔍</div>
          <h2 className="bm-empty__title">No Results</h2>
          <p className="bm-empty__text">No bookmarks match "{search}"</p>
          <button className="filter-chip" onClick={() => setSearch('')}>Clear Search</button>
        </div>
      ) : (
        <>
          {search && (
            <p className="bm-result-count animate-fade-in">
              {filtered.length} result{filtered.length !== 1 ? 's' : ''} for "{search}"
            </p>
          )}
          <div className="bm-grid">
            {filtered.map((article, index) => (
              <BookmarkCard
                key={article.url}
                article={article}
                index={index}
                onRemove={handleRemove}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default BookmarksPage;
